import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { Formik, FieldArray, Form } from "formik";
import AddIcon from "@mui/icons-material/Add";
import color from "../../../../components/utils/Colors";
import Sidebar from "./CourseSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { courseDescriptionValidation } from "../../../../components/schema/schema";
import {
  updateCourseDescription,
  getOneDataFromTable,
} from "../../../../services/services";
import { useSnackbar } from "../../../../components/utils/SnackBar";

const CourseDescriptionForm = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  // courseId is injected by CreateCourseSteps via navigation state
  const courseId = (location.state as any)?.courseId;

  const [formValues, setFormValues] = useState({
    learningObjectives: ["", "", ""],
    whoCanJoin: "",
    requiredSkills: "",
  });

  useEffect(() => {
    const fetchCourseData = async () => {
      if (courseId) {
        try {
          const res = await getOneDataFromTable("course", courseId);
          if (res?.data?.data) {
            const courseData = res.data.data;
            const objectives = courseData.learningObjectives || [];

            // Ensure at least 3 fields are shown even if fewer are saved
            const paddedObjectives = [
              ...objectives,
              ...Array(Math.max(0, 3 - objectives.length)).fill(""),
            ];

            setFormValues({
              learningObjectives: paddedObjectives,
              whoCanJoin: courseData.whoCanJoin || "",
              requiredSkills: courseData.requiredSkills || "",
            });
          }
        } catch (error) {
          console.error("Failed to fetch course details:", error);
          showSnackbar("Failed to fetch course details", "error");
        }
      }
    };
    fetchCourseData();
  }, [courseId]);

  const onSubmit = async (values: typeof formValues) => {
    setLoading(true);
    try {
      console.log(values.learningObjectives);
      await updateCourseDescription(courseId, {
        learningObjectives: values.learningObjectives.filter(
          (obj) => obj.trim() !== "",
        ),
        whoCanJoin: values.whoCanJoin,
        requiredSkills: values.requiredSkills,
      });
      // Always carry courseId forward so downstream pages have it
      navigate("/course-content", { state: { courseId } });
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          "An error occurred while updating course description. Please try again.",
      );
      console.error("Course description update error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: isSmallScreen ? "5px" : "30px",
        gap: "1%",
        marginBottom: "0px",
        paddingBottom: "5%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      {/* <Sidebar /> */}

      <Box
        sx={{
          marginTop: isSmallScreen ? "25px" : "0px",
          flex: 1,
          pl: "3%",
          borderRadius: "16px",
        }}
      >
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={courseDescriptionValidation}
          onSubmit={onSubmit}
        >
          {({ values, handleChange, errors, touched, handleBlur }) => (
            <Form>
              <Box>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    background: color.textColor,
                    textAlign: "center",
                    color: "white",
                    width: "fit-content",
                    margin: "auto",
                    padding: "2px 10px",
                    borderRadius: "4px",
                    mb: 4,
                  }}
                >
                  Describe the Course to the Learner
                </Typography>

                {/* Learning Objectives */}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold", color: color.textColor }}
                >
                  What will students learn from your course?
                </Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ color: color.textColor }}
                >
                  You must enter at least 3 learning objectives that learners
                  can expect to achieve after completing this course.
                </Typography>

                <FieldArray name="learningObjectives">
                  {({ push }) => (
                    <>
                      {values.learningObjectives.map((_, index) => (
                        <Grid item xs={12} key={index}>
                          <TextField
                            className="input-root2"
                            fullWidth
                            name={`learningObjectives[${index}]`}
                            placeholder={`Learning Objective ${index + 1}`}
                            value={values.learningObjectives[index]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(
                              (
                                touched.learningObjectives as
                                  | boolean[]
                                  | undefined
                              )?.[index] && errors.learningObjectives?.[index],
                            )}
                            helperText={
                              (
                                touched.learningObjectives as
                                  | boolean[]
                                  | undefined
                              )?.[index] && errors.learningObjectives?.[index]
                            }
                            margin="normal"
                          />
                        </Grid>
                      ))}

                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          sx={{
                            color: color.textColor,
                            borderRadius: "0px",
                            fontSize: "18px",
                            ml: "auto",
                            mt: 2,
                          }}
                          onClick={() => {
                            const allFilled = values.learningObjectives.every(
                              (obj) => obj.trim() !== "",
                            );
                            if (allFilled) {
                              push("");
                            } else {
                              alert(
                                "Please fill in all existing learning objectives before adding more.",
                              );
                            }
                          }}
                        >
                          <AddIcon /> Add More
                        </IconButton>
                      </Box>
                    </>
                  )}
                </FieldArray>

                {/* Who Can Join */}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold", color: color.textColor, mt: 2 }}
                >
                  Who can enroll in this course?
                </Typography>
                <TextField
                  fullWidth
                  className="input-root2"
                  name="whoCanJoin"
                  placeholder="e.g. No coding experience needed. You will learn from basics."
                  value={values.whoCanJoin}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.whoCanJoin && Boolean(errors.whoCanJoin)}
                  helperText={touched.whoCanJoin && errors.whoCanJoin}
                  margin="normal"
                />

                {/* Required Skills */}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold", color: color.textColor, mt: 2 }}
                >
                  What skills or knowledge are required?
                </Typography>
                <TextField
                  fullWidth
                  className="input-root2"
                  name="requiredSkills"
                  placeholder="Required Skills"
                  value={values.requiredSkills}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.requiredSkills && Boolean(errors.requiredSkills)
                  }
                  helperText={touched.requiredSkills && errors.requiredSkills}
                  margin="normal"
                />

                {/* Action Buttons */}
                <Box display="flex" justifyContent="end" mt={3}>
                  {/* Save — submits the form (calls API then navigates) */}
                  <Button id="button" type="submit" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : "Save & Next"}
                  </Button>

                  {/* Skip — navigate without saving (courseId still carried) */}
                  {/* <Button
                    id="button"
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      navigate("/course-content", { state: { courseId } })
                    }
                  >
                    Skip
                  </Button> */}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default CourseDescriptionForm;
